/* eslint-disable react/require-default-props */
import { Form } from 'components/ui/atoms';
import { CheckoutTestResult } from 'components/ui/organisms/monarch/rules/checkout/types';
import { MonarchDataConsumer } from 'helpers/eventing/monarch/MonarchDataConsumer';
import { getIsStripeCTAPending, useAppSelector } from 'store';
import { useStripeOptions } from 'frontastic/hooks';
import { PayPalForm } from '../paypal-form';
import { AddressForm } from './address-form';
import { addressSchema } from './address-schema';
import { PlaceOrderButton } from './place-order-button';
import { StripeForm } from './stripe-form';
import { AddressSchema, CheckoutFormProps } from './types';

export const PlaceOrderButtonRow = ({
  isCTAPending,
}: {
  isCTAPending: boolean;
}) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 32 }}>
    <PlaceOrderButton isCTAPending={isCTAPending} />
  </div>
);

export const PayPalSection = ({ isMobile = false }: { isMobile?: boolean }) => (
  <div
    className="paypal-section"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 27,
      width: '100%',
    }}
  >
    {/* Conditionally hide PayPal info for mobile */}
    {!isMobile && (
      <div className="paypal-info" style={{ flex: 1 }}>
        <span
          style={{
            fontWeight: 700,
            fontSize: '1.125rem',
            display: 'block',
          }}
        >
          <MonarchDataConsumer<CheckoutTestResult> experimentKey="checkout">
            {(data) => data?.data?.payPalTitleText ?? 'Pay with PayPal'}
          </MonarchDataConsumer>
        </span>
        <span
          style={{
            fontWeight: 400,
            fontSize: '1.0625rem',
            display: 'block',
          }}
        >
          Add your PayPal info through a secure PayPal login.
        </span>
      </div>
    )}
    <div className="paypal-btn-desktop" style={{ width: '50%' }}>
      <PayPalForm />
    </div>
    {/* Mobile: button below info */}
    <div className="paypal-btn-mobile">
      <PayPalForm />
    </div>
  </div>
);

type CheckoutFormWithOptions = CheckoutFormProps & {
  showPlaceOrderButton?: boolean;
  showPayPalSection?: boolean;
  isMobile?: boolean;
};

export const CheckoutForm = ({
  showPlaceOrderButton = true,
  showPayPalSection = true,
  isMobile = false,
  ...props
}: CheckoutFormWithOptions) => {
  const isStripeCTAPending = useAppSelector(getIsStripeCTAPending);

  const { placeStripeOrder } = useStripeOptions();

  const handleSubmit = (address: AddressSchema) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    placeStripeOrder({ address, data: props });
  };

  return (
    <Form
      aria-label="checkout-form"
      className="checkout-test-form"
      onSubmit={handleSubmit}
      validationSchema={addressSchema}
      defaultValues={{
        firstName: '',
        lastName: '',
        address: '',
        unit: '',
        city: '',
        country: '',
        state: '',
        stateCode: 'not-required',
        postalCode: '',
      }}
    >
      <AddressForm isDisabled={isStripeCTAPending} />
      <StripeForm isDisabled={isStripeCTAPending} />
      {/* Place Order Button Row */}
      {showPlaceOrderButton && (
        <PlaceOrderButtonRow isCTAPending={isStripeCTAPending} />
      )}
      {!isMobile && (
        <hr
          style={{
            margin: '32px 0 24px 0',
            border: 0,
            borderTop: '1px solid #e0e0e0',
          }}
        />
      )}
      {/* PayPal Row */}
      {showPayPalSection && <PayPalSection isMobile={isMobile} />}
      {isMobile && (
        <hr
          style={{
            margin: '32px 0 24px 0',
            border: 0,
            borderTop: '1px solid #e0e0e0',
          }}
        />
      )}
    </Form>
  );
};
